<template>
  <aside v-if="visible" class="payment-return-handler" role="status" aria-live="polite">
    <div class="payment-return-handler__copy">
      <strong>{{ title }}</strong>
      <span>{{ detail }}</span>
    </div>
    <button
      class="payment-return-handler__action"
      type="button"
      :disabled="checking"
      @click="checkPaymentStatus"
    >
      {{ checking ? '正在确认...' : '刷新状态' }}
    </button>
  </aside>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { tradeApi } from '@/api/modules'
import type { OrderStatus, PendingPaymentReturnState } from '@/types/trade'
import { message } from '@/utils/message/message'
import {
  clearPendingPaymentReturn,
  loadPendingPaymentReturn,
} from '@/utils/payment/payment'

const route = useRoute()
const router = useRouter()

const visible = ref(false)
const checking = ref(false)
const needsAttention = ref(false)
const pendingState = ref<PendingPaymentReturnState | null>(null)

const orderNo = computed(() =>
  typeof route.query['order_no'] === 'string' ? route.query['order_no'] : ''
)
const hasPaymentReturn = computed(
  () => typeof route.query['pay_result'] === 'string' && Boolean(orderNo.value)
)
const title = computed(() =>
  checking.value ? '正在确认支付结果' : needsAttention.value ? '支付结果仍在同步' : '支付结果确认'
)
const detail = computed(() =>
  checking.value
    ? `正在查询订单 ${orderNo.value}，确认后会自动刷新当前页面`
    : '可以稍后点击刷新状态，订单到账以服务端查询结果为准'
)

const MAX_POLL_TIMES = 15
const POLL_INTERVAL = 1500
let activeGeneration = 0

const delay = (milliseconds: number): Promise<void> =>
  new Promise(resolve => window.setTimeout(resolve, milliseconds))

const queryOrderStatus = async (): Promise<OrderStatus> => {
  const response = pendingState.value?.status_token
    ? await tradeApi.getDebtRecoveryOrderStatus(orderNo.value, pendingState.value.status_token)
    : await tradeApi.getOrderDetail(orderNo.value)
  return response.data.status
}

const reloadCleanBusinessPage = async (status: OrderStatus) => {
  clearPendingPaymentReturn(orderNo.value)
  if (status === 'paid') {
    message.success('支付成功，页面数据已更新')
  } else if (status === 'refunded') {
    message.warning('订单款项已退回')
  } else {
    message.warning('订单未完成支付')
  }
  const cleanQuery = { ...route.query }
  delete cleanQuery['pay_result']
  delete cleanQuery['order_no']
  await router.replace({ path: route.path, query: cleanQuery, hash: route.hash })
  window.location.reload()
}

const checkPaymentStatus = async () => {
  if (checking.value || !hasPaymentReturn.value || route.path === '/payment-result') return
  const generation = ++activeGeneration
  checking.value = true
  needsAttention.value = false
  visible.value = true
  pendingState.value = loadPendingPaymentReturn(orderNo.value)
  try {
    for (let attempt = 0; attempt < MAX_POLL_TIMES; attempt += 1) {
      const status = await queryOrderStatus()
      if (generation !== activeGeneration) return
      if (
        status === 'paid' ||
        status === 'cancelled' ||
        status === 'refunded'
      ) {
        await reloadCleanBusinessPage(status)
        return
      }
      if (attempt < MAX_POLL_TIMES - 1) await delay(POLL_INTERVAL)
    }
    if (generation === activeGeneration) needsAttention.value = true
  } catch {
    if (generation === activeGeneration) {
      needsAttention.value = true
      message.warning('暂时无法确认支付结果，请稍后刷新状态')
    }
  } finally {
    if (generation === activeGeneration) checking.value = false
  }
}

watch(
  () => route.fullPath,
  () => {
    activeGeneration += 1
    if (!hasPaymentReturn.value || route.path === '/payment-result') {
      visible.value = false
      checking.value = false
      needsAttention.value = false
      return
    }
    void checkPaymentStatus()
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  activeGeneration += 1
})
</script>

<style lang="scss" scoped>
.payment-return-handler {
  position: fixed;
  top: 18px;
  left: 50%;
  z-index: 5000;
  display: flex;
  width: min(calc(100% - 32px), 620px);
  padding: 14px 16px;
  background: $color-bg-card;
  border: $border-cartoon-light;
  border-radius: $radius-cartoon-md;
  box-shadow: $shadow-brutal-md;
  transform: translateX(-50%);
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  &__copy {
    display: grid;
    gap: 4px;

    strong {
      color: $color-text-primary;
    }

    span {
      font-size: $font-size-small;
      color: $color-text-secondary;
    }
  }

  &__action {
    flex: none;
    min-height: 36px;
    padding: 0 16px;
    color: $color-text-white;
    background: $gradient-primary;
    border: $border-cartoon-light;
    border-radius: $radius-pill;
    cursor: pointer;

    &:disabled {
      cursor: wait;
      opacity: 0.65;
    }
  }

  @include phone {
    align-items: stretch;
    flex-direction: column;

    &__action {
      width: 100%;
    }
  }
}
</style>
